/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   PolicyList.tsx
 *     Description     :   List of maintenance policies with edit/delete actions
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-01-19 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.11.0 (Specification Testing Standards Edition)
 *     Feature         :   028-lakehouse-maintenance
 * =========================================================================================
 */

import React, { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Card, Badge, Button, Spinner, AppTooltip } from '../ui';
import { AppModal } from '../ui/AppModal';
import { usePolicies, useDeletePolicy } from '../../hooks/useMaintenanceData';
import type { MaintenancePolicy } from '../../../../shared/types/maintenance';
import PolicyForm from './PolicyForm';
import { DualScrollTable, usePagination, PaginationControls } from '../common/TableControls';

const iconBtn =
  'inline-flex items-center justify-center rounded-[var(--radius-md)] p-1.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)]';

export const PolicyList: React.FC = () => {
  const { data: policies, isLoading } = usePolicies();
  const deleteMutation = useDeletePolicy();

  const [editingPolicy, setEditingPolicy] = useState<MaintenancePolicy | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<MaintenancePolicy | null>(null);

  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } =
    usePagination(policies?.data ?? []);

  const handleEdit = (policy: MaintenancePolicy) => {
    setEditingPolicy(policy);
  };

  const handleDelete = async () => {
    if (deleteConfirm) {
      await deleteMutation.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Spinner />
      </div>
    );
  }

  if (!policies || !policies.data || policies.data.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-[var(--color-text-secondary)]">No policies configured</p>
      </div>
    );
  }

  return (
    <>
      <Card padding="none" className="overflow-hidden">
        <DualScrollTable>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
                <th className="px-3 py-2 text-left font-semibold text-[var(--color-text-primary)]">Policy Name</th>
                <th className="px-3 py-2 text-left font-semibold text-[var(--color-text-primary)]">Description</th>
                <th className="px-3 py-2 text-left font-semibold text-[var(--color-text-primary)]">Optimize Threshold</th>
                <th className="px-3 py-2 text-left font-semibold text-[var(--color-text-primary)]">Vacuum Retention</th>
                <th className="px-3 py-2 text-left font-semibold text-[var(--color-text-primary)]">Status</th>
                <th className="px-3 py-2 text-right font-semibold text-[var(--color-text-primary)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((policy: MaintenancePolicy) => (
                <tr
                  key={policy.id}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-subtle)]"
                >
                  <td className="px-3 py-2">
                    <span className="text-sm font-medium text-[var(--color-text-primary)]">
                      {policy.name}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-sm text-[var(--color-text-secondary)]">
                      {policy.description || <em>No description</em>}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={policy.optimizeThresholdPct > 0 ? 'info' : 'default'}>
                      {`${policy.optimizeThresholdPct}%`}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      {policy.vacuumRetentionDays > 0 && (
                        <Badge variant="default" isOutlined>
                          {`${policy.vacuumRetentionDays} days`}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={policy.isActive ? 'success' : 'default'}>
                      {policy.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <AppTooltip title="Edit policy">
                      <button className={iconBtn} onClick={() => handleEdit(policy)} aria-label="Edit policy">
                        <Pencil className="h-4 w-4" />
                      </button>
                    </AppTooltip>
                    <AppTooltip title="Delete policy">
                      <button
                        className={`${iconBtn} hover:text-[var(--color-status-danger)]`}
                        onClick={() => setDeleteConfirm(policy)}
                        aria-label="Delete policy"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </AppTooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DualScrollTable>
        <PaginationControls
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          start={start}
          shown={pageItems.length}
          onPage={setPage}
          onPageSize={changeSize}
        />
      </Card>

      {/* Edit Dialog */}
      {editingPolicy && (
        <PolicyForm
          open={true}
          onClose={() => setEditingPolicy(null)}
          policy={editingPolicy}
          mode="edit"
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AppModal
        title="Confirm Delete"
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-[var(--color-text-primary)]">
          Are you sure you want to delete the policy "{deleteConfirm?.name}"?
        </p>
        <p className="mt-2 text-sm text-[var(--color-status-danger)]">
          This will unassign the policy from all lakehouses.
        </p>
      </AppModal>
    </>
  );
};

export default PolicyList;
